
import {
  defineComponent, ref, computed, PropType, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Checkbox from 'primevue/checkbox';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';
import type { SelectableItem } from '@/composables/import/types';

export default defineComponent({
  name: 'ItemSelectorStep',

  components: {
    Checkbox,
    TemplateHtmlPreviewSlider,
  },

  props: {
    /** Items to display for selection */
    modelValue: {
      type: Array as PropType<SelectableItem[]>,
      required: true,
    },

    /** Import type (campaign_automation, campaign_bulk, template) */
    importType: {
      type: String,
      required: true,
    },

    /** Loading state */
    loading: {
      type: Boolean,
      default: false,
    },

    /** Error message */
    error: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue', 'valid-change'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Flag to prevent watch from re-triggering after our own emit
    let isInternalUpdate = false;

    // Local copy of items
    const items = ref<SelectableItem[]>([]);

    // Select all state
    const selectAll = ref(true);

    // HTML preview slider state
    const htmlSliderItem = ref<SelectableItem | null>(null);

    const openHtmlPreview = (item: SelectableItem) => {
      htmlSliderItem.value = item;
    };

    const closeHtmlPreview = () => {
      htmlSliderItem.value = null;
    };

    // Count of selected items
    const selectedCount = computed(() => items.value.filter((item) => item.selected).length);

    // Validation: at least one item must be selected
    const isValid = computed(() => selectedCount.value > 0);

    // Get icon based on import type
    const getItemIcon = (): string => {
      switch (props.importType) {
        case 'campaign_automation':
          return 'fa-light fa-robot';
        case 'campaign_bulk':
          return 'fa-light fa-paper-plane';
        case 'template':
          return 'fa-light fa-palette';
        default:
          return 'fa-light fa-file';
      }
    };

    // Get human-readable label for import type
    const getItemTypeLabel = (): string => {
      switch (props.importType) {
        case 'campaign_automation':
          return t('import.itemSelector.types.automation');
        case 'campaign_bulk':
          return t('import.itemSelector.types.bulk');
        case 'template':
          return t('import.itemSelector.types.template');
        default:
          return t('import.itemSelector.types.item');
      }
    };

    // Update select all checkbox state based on items
    const updateSelectAllState = () => {
      selectAll.value = items.value.length > 0 && items.value.every((item) => item.selected);
    };

    // Emit changes to parent
    const emitChanges = () => {
      isInternalUpdate = true;
      const updatedItems: SelectableItem[] = items.value.map((item) => ({
        id: item.id,
        name: item.name,
        selected: item.selected,
        preview: item.preview,
        previews: item.previews,
        itemType: item.itemType,
      }));
      emit('update:modelValue', updatedItems);
      emit('valid-change', isValid.value);
      setTimeout(() => {
        isInternalUpdate = false;
      }, 0);
    };

    // Toggle item selection
    const toggleItem = (item: SelectableItem) => {
      const index = items.value.findIndex((i) => i.id === item.id);
      if (index !== -1) {
        items.value[index].selected = !items.value[index].selected;
        updateSelectAllState();
        emitChanges();
      }
    };

    // Handle select all change
    const onSelectAllChange = () => {
      items.value.forEach((i) => {
        // eslint-disable-next-line no-param-reassign
        i.selected = selectAll.value;
      });
      emitChanges();
    };

    // Sync with parent modelValue
    watch(
      () => props.modelValue,
      (newVal, oldVal) => {
        if (isInternalUpdate) {
          return;
        }

        if (!oldVal || items.value.length === 0) {
          items.value = newVal.map((item) => ({ ...item }));
          updateSelectAllState();
          return;
        }

        const currentIds = items.value.map((i) => i.id).sort().join(',');
        const newIds = newVal.map((i) => i.id).sort().join(',');

        if (currentIds !== newIds) {
          items.value = newVal.map((item) => ({ ...item }));
          updateSelectAllState();
        }
      },
      { immediate: true, deep: true },
    );

    // Emit validity on mount
    watch(isValid, (newVal) => {
      emit('valid-change', newVal);
    }, { immediate: true });

    return {
      t,
      items,
      selectAll,
      selectedCount,
      isValid,
      getItemIcon,
      getItemTypeLabel,
      toggleItem,
      onSelectAllChange,
      htmlSliderItem,
      openHtmlPreview,
      closeHtmlPreview,
    };
  },
});
